import moment from 'moment';
import normalizeEventDate from './normalizeEventDate';

const isValidDateOnly = (s) =>
  typeof s === 'string' && moment(s, 'YYYY-MM-DD', true).isValid();

const toDateOnly = (s) => {
  if (typeof s !== 'string' || !s) return null;
  const m = moment(s, 'YYYY-MM-DD', true);
  if (m.isValid()) return m.format('YYYY-MM-DD');
  const loose = moment(s);
  return loose.isValid() ? loose.format('YYYY-MM-DD') : null;
};

/**
 * Returns a normalized { startDate, endDate } pair (YYYY-MM-DD strings) for any
 * event date shape. Both the date label and the live/finished badge must use
 * this helper so their interpretations of an event's lifetime stay aligned.
 *
 *  - fixed events:     start = date.startDate, end = date.endDate
 *  - segmented events: start = earliest day.date, end = latest day.date
 */
const getEventDateRange = (rawDate) => {
  const date = normalizeEventDate(rawDate);
  if (!date || !date.type) return null;

  if (date.type === 'fixed') {
    const startDate = toDateOnly(date.startDate);
    const endDate = toDateOnly(date.endDate) || startDate;
    if (!startDate || !endDate) return null;
    if (endDate < startDate) return null;
    return { startDate, endDate };
  }

  if (date.type === 'segmented' && Array.isArray(date.days) && date.days.length) {
    const dates = date.days
      .map((d) => toDateOnly(d?.date))
      .filter(Boolean)
      .sort();
    if (!dates.length) return null;
    return { startDate: dates[0], endDate: dates[dates.length - 1] };
  }

  return null;
};

const getTodayDateOnly = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const isEventOngoing = (rawDate, today = getTodayDateOnly()) => {
  const range = getEventDateRange(rawDate);
  if (!range) return false;
  if (!isValidDateOnly(today)) return false;
  return today >= range.startDate && today <= range.endDate;
};

export { getEventDateRange, getTodayDateOnly, isEventOngoing };
export default getEventDateRange;
